import axios from 'axios';
import store from '../reducers/store';
import { decryptData, encryptData } from './crypto';
import { clearAllCookies } from './cookieHelper';
import { getUserInfo } from './tokenUtils';
import {
	LOGIN_API,
	GET_ZTP__BANNER_DETAILS,
	ENDPOINT_URL,
	TOKEN_EXPIRED,
	LOGIN_APRECOMM_URL,
	UPLOAD_LICENCE_API
} from '../constants/commonConstants';
import { logout } from '../actions/userActions';

// ---- Helpers ---------------------------------------------------------------

const AUTH_HEADER_KEY = 'authorization';

/**
 * Reads the current token from encrypted `userInfo` in localStorage.
 * Falls back to existing helper so older code paths keep working.
 *
 * @return {string|null} The current authorization token, or null if unavailable.
 */
const readTokenFromStorage = () => {
	try {
		const stored = localStorage.getItem('userInfo');
		if (!stored) return getUserInfo() || null;

		const parsed = JSON.parse(decryptData(stored));
		return parsed?.authorizationToken || getUserInfo() || null;
	} catch {
		return getUserInfo() || null;
	}
};

/**
 * Persists a rotated token into localStorage.
 *
 * @param {string} nextToken The new authorization token to store.
 * @return {void}
 */
const writeTokenToStorage = (nextToken) => {
	if (!nextToken) return;

	try {
		const raw = localStorage.getItem('userInfo');
		let userInfo = null;
		if (raw) {
			try {
				userInfo = JSON.parse(decryptData(raw));
			} catch {
				userInfo = null;
			}
		}

		const updated = {
			...(userInfo || {}),
			authorizationToken: nextToken,
		};

		localStorage.setItem('userInfo', encryptData(JSON.stringify(updated)));
	} catch {
		// ignore storage errors
	}
};

/**
 * Whether the given URL should *not* receive an Authorization header.
 *
 * @param {string} [url=''] The request URL (absolute or relative).
 * @return {boolean} True if the URL is auth‑free; otherwise false.
 */
const isAuthFreeUrl = (url = '') => {
	if (!url) return false;
	const absolute = url.startsWith('http') ? url : `${ENDPOINT_URL}${url}`;
	return (
		absolute.includes(LOGIN_API) ||
		absolute.includes(GET_ZTP__BANNER_DETAILS) ||
		absolute.includes(LOGIN_APRECOMM_URL) ||
		absolute.includes(UPLOAD_LICENCE_API)
	);
};

/**
 * Best‑effort detection of a "true" token‑expiry error from an encrypted payload.
 *
 * @param {object} error Axios error object from a failed response.
 * @return {boolean} True if the error represents a token‑expiry condition.
 */
const isTokenExpiredError = (error) => {
	const status = error?.response?.status;
	if (status !== 401) return false;

	const data = error?.response?.data;
	if (!data) return true; // fall back: any 401 is treated as expired

	try {
		// All API responses are encrypted when 'X-Encrypt' is used.
		const decrypted = decryptData(data);
		const parsed = JSON.parse(decrypted);
		// Backend typically returns { status: 401, error: TOKEN_EXPIRED }
		if (parsed?.status === 401 && parsed?.error === TOKEN_EXPIRED) {
			return true;
		}
	} catch {
		// If we can't decrypt/parse, assume it's an auth failure.
		  console.warn('Token expiry parse failed');

	}

	return true;
};


// ---- Interceptors ----------------------------------------------------------
// Attach common headers (X-Encrypt, Content-Type) and Authorization header automatically to every outgoing API request.
axios.interceptors.request.use(
	(config) => {
		// Ensure headers object exists
		if (!config?.headers) {
			config.headers = {};
		}

		// Automatically add X-Encrypt header if not already set
		if (!config.headers['X-Encrypt'] && !config.headers['x-encrypt']) {
			config.headers['X-Encrypt'] = 'true';
		}

		// Automatically add Content-Type header if not already set.
		// BUT: Skip for FormData (needs multipart/form-data), methods that
		// truly don't have bodies (GET/HEAD), and requests without a meaningful body.
		const isFormData = config.data instanceof FormData;
		const method = config?.method?.toUpperCase();
		// Allow DELETE to have a body (needed for APIs like deleteRoleBased)
		const methodWithoutBody = ['GET', 'HEAD'].includes(method);
		const hasData =
			config.data !== undefined &&
			config.data !== null &&
			!(
				typeof config.data === 'string' &&
				config.data.length === 0
			) &&
			!(
				typeof config.data === 'object' &&
				!Array.isArray(config.data) &&
				Object.keys(config.data).length === 0
			);

		const existingContentType =
			config.headers['Content-Type'] || config.headers['content-type'];

		// Only add Content-Type if:
		// 1. No Content-Type/header is already present (any casing)
		// 2. Not FormData (FormData sets its own boundary)
		// 3. Not a method that truly has no body (GET/HEAD)
		// 4. There is actual payload data (hasData)
		if (!existingContentType && !isFormData && !methodWithoutBody && hasData) {
			config.headers['Content-Type'] = 'application/json';
		}

		// Skip auth for login/logout/OTP endpoints
		if (isAuthFreeUrl(config.url)) {
			return config;
		}

		// Attach Authorization header if not already present or if it's undefined/empty
		const existingAuth = config.headers.Authorization || config.headers[AUTH_HEADER_KEY];
		if (!existingAuth || existingAuth === 'undefined' || existingAuth === 'null') {
			const token = readTokenFromStorage();
			if (token) {
				config.headers.Authorization = token;
			}
		}

		return config;
	},
	(error) => Promise.reject(error)
);

// Centralized 401 + token‑rotation handling.
let isRedirectingOn401 = false;
let redirectTimeoutId = null;

/**
 * Resets the isRedirectingOn401 flag after a timeout period.
 * This ensures that if a redirect fails or multiple 401 errors occur,
 * the application can recover and handle subsequent token expiration.
 */
const resetRedirectFlag = () => {
	if (redirectTimeoutId) {
		clearTimeout(redirectTimeoutId);
	}
	// Reset flag after 5 seconds to allow for redirect/navigation to complete
	// If navigation hasn't completed by then, allow handling of new 401 errors
	redirectTimeoutId = setTimeout(() => {
		isRedirectingOn401 = false;
		redirectTimeoutId = null;
	}, 5000);
};

axios.interceptors.response.use(
	(response) => {
		// If backend rotated the token, update localStorage + Redux.
		const rotatedToken =
			response?.headers?.[AUTH_HEADER_KEY] ||
			response?.headers?.[AUTH_HEADER_KEY.toUpperCase()];

		if (rotatedToken && rotatedToken !== readTokenFromStorage()) {
			writeTokenToStorage(rotatedToken);
		}

		return response;
	},
	(error) => {
		if (!isTokenExpiredError(error) || isRedirectingOn401) {
			return Promise.reject(error);
		}

		isRedirectingOn401 = true;

		try {
			localStorage.setItem('userlogout', TOKEN_EXPIRED);
		} catch {
			// ignore
		}

		// Clear all storage and cookies immediately on 401
		try {
			localStorage.clear();
			clearAllCookies();
		} catch {
			// ignore cookie/storage errors
		}

		// Let Redux know so any listeners can react; navigation is handled by interceptor
		// Pass skipRedirect=true to prevent double redirect (clear() will skip its redirect)
		try {
			store.dispatch(logout(undefined, undefined, true));
		} catch {
			// ignore
		}
		
		// Ensure cookies are cleared again after logout dispatch (in case logout API sets new cookies)
		try {
			clearAllCookies();
		} catch {
			// ignore
		}

		const currentPath = window.location.pathname;
		const excludedPaths = [
			'/',
			'/signin',
			'/signup',
			'/reset/password',
			'/license/upload',
			'/signin/otpverify',
			'/signup/otp/verify',
		];

		if (!excludedPaths.includes(currentPath)) {
			window.location.href = '/';
		}

		// Reset the flag after a timeout to allow recovery from failed redirects
		resetRedirectFlag();

		return Promise.reject(error);
	}
);

